import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../common/Card';
import Button from '../common/Button';

const AttendeeDetailTest = () => {
  const [attendeeId, setAttendeeId] = useState('1');
  const [eventId, setEventId] = useState('1');
  const [testResults, setTestResults] = useState([]);
  const navigate = useNavigate();

  const addResult = (test, status, message) => {
    setTestResults(prev => [...prev, { test, status, message, timestamp: new Date().toLocaleTimeString() }]);
  };

  const testDirectURL = () => {
    const url = `/attendee/${attendeeId}${eventId ? `?event=${eventId}` : ''}`;
    addResult('Direct URL Navigation', 'info', `Navigating to: ${url}`);
    navigate(url);
  };

  const testAPIEndpoint = async () => {
    try {
      addResult('API Test', 'info', 'Testing API endpoint...');
      
      const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';
      const url = `${baseUrl}/attendees/${attendeeId}/public-no-auth`;
      
      addResult('API Test', 'info', `Calling: ${url}`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        addResult('API Test', 'success', `API Success: ${JSON.stringify(data, null, 2)}`);
      } else {
        addResult('API Test', 'error', `API Error: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      addResult('API Test', 'error', `API Exception: ${error.message}`);
    }
  };

  const testQRCodeURL = () => {
    const baseUrl = window.location.origin;
    const url = `${baseUrl}/attendee/${attendeeId}${eventId ? `?event=${eventId}` : ''}`;
    addResult('QR Code URL', 'info', `Generated QR URL: ${url}`);
    
    // Open in new tab
    window.open(url, '_blank');
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <Card>
        <Card.Content>
          <h2 className="text-2xl font-bold mb-4">🔍 Attendee Detail Debug Test</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Controls */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attendee ID
                </label>
                <input
                  type="text"
                  value={attendeeId}
                  onChange={(e) => setAttendeeId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter attendee ID"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event ID (optional)
                </label>
                <input
                  type="text"
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter event ID"
                />
              </div>
            </div>

            {/* Test Buttons */}
            <div className="space-y-3">
              <Button onClick={testAPIEndpoint} className="w-full">
                🧪 Test API Endpoint
              </Button>
              
              <Button onClick={testDirectURL} variant="secondary" className="w-full">
                🔗 Test Direct Navigation
              </Button>
              
              <Button onClick={testQRCodeURL} variant="outline" className="w-full">
                📱 Test QR URL (New Tab)
              </Button>
              
              <Button onClick={clearResults} variant="ghost" className="w-full">
                🗑️ Clear Results
              </Button>
            </div>
          </div>

          {/* Current Environment Info */}
          <div className="bg-gray-100 p-4 rounded-lg mb-4">
            <h3 className="font-semibold mb-2">Environment Info:</h3>
            <div className="text-sm space-y-1">
              <p><strong>API URL:</strong> {process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}</p>
              <p><strong>Current Origin:</strong> {window.location.origin}</p>
              <p><strong>Current Path:</strong> {window.location.pathname}</p>
              <p><strong>Node ENV:</strong> {process.env.NODE_ENV}</p>
            </div>
          </div>
        </Card.Content>
      </Card>

      {/* Test Results */}
      {testResults.length > 0 && (
        <Card>
          <Card.Content>
            <h3 className="text-lg font-semibold mb-4">Test Results</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => (
                <div 
                  key={index}
                  className={`p-3 rounded-lg border-l-4 ${
                    result.status === 'success' ? 'bg-green-50 border-green-500' :
                    result.status === 'error' ? 'bg-red-50 border-red-500' :
                    'bg-blue-50 border-blue-500'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{result.test}</p>
                      <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{result.message}</p>
                    </div>
                    <span className="text-xs text-gray-500">{result.timestamp}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>
      )}

      {/* Instructions */}
      <Card>
        <Card.Content>
          <h3 className="text-lg font-semibold mb-3">🎯 Testing Instructions</h3>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li><strong>Test API Endpoint:</strong> Checks if the backend API is responding correctly</li>
            <li><strong>Test Direct Navigation:</strong> Uses React Router to navigate to the attendee detail page</li>
            <li><strong>Test QR URL:</strong> Opens the attendee detail URL in a new tab (simulates QR scan)</li>
            <li><strong>Check Results:</strong> Look for any errors or unexpected redirects</li>
          </ol>
          
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Expected Behavior:</strong> All tests should work without redirecting to dashboard. 
              If you're getting redirected, check the console for errors and the test results above.
            </p>
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default AttendeeDetailTest;
